import { Clock, Gift, Search, type LucideIcon } from "lucide-react"

import CoverHero from "@/assets/img/soulmate/cover-hero.png"
import { Button } from "@/components/ui/button"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import { usePreloadQuizImages } from "../../hooks/use-preload-quiz-images"
import type { StepComponentProps } from "./step-contract"

/** lucide icons for the cover benefit tiles (clock / search / gift). */
const ICONS: Record<string, LucideIcon> = {
  clock: Clock,
  search: Search,
  gift: Gift,
}

/**
 * Soulmate Sketch cover (Figma Step0): a full-width hero of a couple holding the
 * sketch, the value-prop title/subtitle, a dark-surface card holding three
 * benefit tiles + the CTA, and the policy links. All content comes from config;
 * the CTA just calls `onAdvance`.
 */
export function SoulmateCoverStep({ step, onAdvance }: StepComponentProps) {
  const features = step.features ?? []

  // Warm the later steps' images while the user is on this first screen, so the
  // quiz doesn't flash-load each image on step entry.
  usePreloadQuizImages("soulmate")

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light">
      <img
        src={CoverHero}
        alt=""
        className="h-[40dvh] max-h-[364px] min-h-[190px] w-full shrink-0 object-cover object-top"
      />

      <div className="flex flex-1 flex-col px-4 pb-safe">
        <div className="flex flex-col gap-5">
          <div className="space-y-2 text-center">
            <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
            {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
          </div>

          <div className="flex gap-1 rounded-[24px] bg-dark-surface p-1">
            {features.map((feature) => {
              const Icon = ICONS[feature.icon] ?? Clock
              const parts = feature.label.split(" ")
              const head = parts.slice(0, -1).join(" ")
              const tail = parts[parts.length - 1]
              return (
                <div
                  key={feature.label}
                  className="flex flex-1 flex-col items-center justify-end gap-2 rounded-[20px] bg-purple-light/10 px-2 py-3"
                >
                  <Icon className="size-6 text-main-purple" strokeWidth={1.5} />
                  <span className="text-center text-sm leading-tight text-purple-light">
                    {head && (
                      <>
                        {head}
                        <br />
                      </>
                    )}
                    {tail}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-6">
          <Button variant="gradient" size="xl" className="w-full" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>

          <div className="flex justify-between px-1 text-center text-xs text-purple-light/40">
            <span className="flex-1">
              <LegalLink to={AppRoutes.PRIVACY}>Privacy Policy</LegalLink>
            </span>
            <span className="flex-1">
              <LegalLink to={AppRoutes.TERMS}>Terms of use</LegalLink>
            </span>
            <span className="flex-1">
              <LegalLink to={AppRoutes.COOKIES}>Cookie Policy</LegalLink>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
